import React, { useState, useRef, useEffect } from 'react';
import { MicrophoneIcon, StopIcon } from '@heroicons/react/24/outline';
import { notify } from '../../utils/notify.jsx';

export default function SpeechToText({ onTranscriptionComplete }) {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [isSupported, setIsSupported] = useState(true);
  const recognitionRef = useRef(null);
  const isIntentionalStopRef = useRef(false);
  const transcriptRef = useRef('');
  const onTranscriptionCompleteRef = useRef(onTranscriptionComplete);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window['webkitSpeechRecognition'];
    
    if (!SpeechRecognition) {
      setIsSupported(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      console.log('🎤 Speech recognition started');
      setIsListening(true);
      notify.info('🎤 Listening for speech...', { toastId: 'speech-listening', autoClose: false });
    };

    recognition.onresult = (event) => {
      console.log('📝 Speech result received');
      let interim = '';
      let final = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcriptSegment = event.results[i][0].transcript;

        if (event.results[i].isFinal) {
          final += transcriptSegment + ' ';
        } else {
          interim += transcriptSegment;
        }
      }

      // Update the textarea in real-time with interim results
      const liveText = transcriptRef.current + final + interim;
      if (onTranscriptionCompleteRef.current) {
        // Call the callback to update the textarea in real-time
        onTranscriptionCompleteRef.current(liveText);
      }

      setInterimTranscript(interim);
      if (final) {
        transcriptRef.current += final;
        setTranscript(transcriptRef.current);
      }
    };

    recognition.onerror = (event) => {
      console.error('❌ Speech recognition error:', event.error);
      
      if (event.error === 'aborted' || isIntentionalStopRef.current) {
        isIntentionalStopRef.current = false;
        return;
      }
      
      let errorMessage = 'Speech recognition error occurred';
      
      if (event.error === 'no-speech') {
        errorMessage = 'No speech detected. Please try again.';
      } else if (event.error === 'network') {
        errorMessage = 'Network error. Please check your connection.';
      } else if (event.error === 'permission-denied') {
        errorMessage = 'Microphone permission denied. Please allow microphone access.';
      }
      
      notify.error(errorMessage, { toastId: 'speech-error' });
      setIsListening(false);
    };

    recognition.onend = () => {
      console.log('🛑 Speech recognition ended');
      setIsListening(false);
      setInterimTranscript('');
      
      // Dismiss the listening toast
      notify.dismiss('speech-listening');
      
      const finalTranscript = transcriptRef.current.trim();
      if (finalTranscript) {
        console.log('📤 Calling onTranscriptionComplete with:', finalTranscript);
        if (onTranscriptionCompleteRef.current) {
          onTranscriptionCompleteRef.current(finalTranscript);
        }
        notify.success('✓ Transcription complete', { toastId: 'speech-success' });
      }
      
      transcriptRef.current = '';
      setTranscript('');
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        isIntentionalStopRef.current = true;
        try {
          recognitionRef.current.abort();
        } catch (e) {
          console.log('Cleanup abort error (expected):', e.message);
        }
      }
    };
  }, []);

  const startListening = () => {
    if (recognitionRef.current && !isListening) {
      try {
        transcriptRef.current = '';
        setTranscript('');
        setInterimTranscript('');
        isIntentionalStopRef.current = false;
        recognitionRef.current.start();
      } catch (error) {
        console.error('Error starting recognition:', error);
        notify.error('Failed to start speech recognition', { toastId: 'speech-start-error' });
      }
    }
  };

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      isIntentionalStopRef.current = true;
      recognitionRef.current.stop();
    }
  };

  return (
    <>
      {/* Mic button next to label */}
      {!isSupported ? (
        <div className="group relative inline-flex">
          <button
            disabled
            title="Speech-to-text is not supported in your browser"
            className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-300 text-gray-500 cursor-not-allowed shadow-md"
          >
            <MicrophoneIcon className="h-4 w-4" />
          </button>
          {/* Tooltip on hover */}
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
            Speech-to-text requires Chrome, Edge, or Safari
          </div>
        </div>
      ) : !isListening ? (
        <button
          onClick={startListening}
          title="Start speech-to-text"
          className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 transition-colors shadow-md hover:shadow-lg"
        >
          <MicrophoneIcon className="h-4 w-4" />
        </button>
      ) : (
        <button
          onClick={stopListening}
          title="Stop listening"
          className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 transition-colors shadow-md hover:shadow-lg animate-pulse"
        >
          <StopIcon className="h-4 w-4" />
        </button>
      )}
    </>
  );
}
